import * as dateFns from "date-fns";
import { createUrn, UrnResource } from "@santa/common/lib/utils/urn";
import { ISelectOption } from "@santa/common/lib/utils/form";
import { earliestLetterSendDate, lastLetterSendDate } from "@santa/common/lib/products/letter";
import { Field, FormikErrors, FormikTouched } from "formik";
import { Col, Row } from "react-grid-system";
import * as yup from "yup";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { ContentPanel } from "../../../../atoms/content-panel";
import { FormFieldAge } from "../../../../molecules/form/form-fields/form-field-age";
import { FormFieldFirstNameFreeText } from "../../../../molecules/form/form-fields/form-field-first-name-free-text";
import { FormFieldLastName } from "../../../../molecules/form/form-fields/form-field-last-name";
import { AddressForm } from "../../../forms/address-form";
import { FormFieldBoyGirl } from "../../../../molecules/form/form-fields/form-field-boy-girl";
import { FormFieldDate } from "../../../../molecules/form/form-fields/form-field-date";
import { Heading5, Paragraph } from "../../../../atoms/text";
import { FormFieldPhoneNumber } from "../../../../molecules/form/form-fields/form-field-phone-number";
import { FormFieldMessageTime } from "../../../..//molecules/form/form-fields/form-field-message-time";
import { FormFieldWrapper } from "../../../form-field-wrapper";
import { FormikFormInputSelect } from "../../../../molecules/form/form-input-select";

export const validationRules = {
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  ageYears: yup
    .number()
    .min(0)
    .max(200, "Are they really that old?!")
    .test(
      "requiredTest",
      "Enter at least one of years or months (y)",
      (value: number | undefined, context): boolean =>
        typeof context.parent.ageMonths !== "undefined" ? true : !!value,
    )
    .nullable()
    .transform((value: string, originalValue: string) =>
      originalValue.trim() === "" ? null : value,
    ),
  ageMonths: yup
    .number()
    .min(0)
    .max(36, "The letter will read better if you use years")
    .test(
      "requiredTest",
      "Enter at least one of years or months (m)",
      (value: number | undefined, context): boolean =>
        typeof context.parent.ageYears !== "undefined" ? true : !!value,
    )
    .nullable()
    .transform((value: string, originalValue: string) =>
      originalValue.trim() === "" ? null : value,
    ),
  boyGirl: yup.string().nullable().required("Required"),
  addressLine1: yup.string().required("Required"),
  addressLine2: yup.string(),
  addressTown: yup.string().required("Required"),
  addressCounty: yup.string(),
  addressUsStateUrn: yup.string().when("addressCountryUrn", {
    is: (addressCountryUrn: string) => addressCountryUrn === createUrn(UrnResource.COUNTRY, "us"),
    then: yup.string().required(),
  }),
  addressPostcode: yup.string().required("Required"),
  addressCountryUrn: yup.string().required("Required"),
  date: yup.date(),
  phoneCountryUrn: yup.string().required("Required when a phone number is provided"),
  phoneTimezoneUrn: yup.string().required("Required"),
  phoneNumber: yup.string().max(20, "Maximum 20 characters").required("Required"),
  textTimeDate: yup.date().required("Required"),
  textTimeTime: yup.string().required("Required"),
};

export interface IDefaultValuesModel {
  firstName?: string;
  lastName?: string;
  ageYears?: string;
  ageMonths?: string;
  boyGirl?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressTown?: string;
  addressCounty?: string;
  addressUsStateUrn?: string;
  addressPostcode?: string;
  addressCountryUrn?: string;
  date?: Date;
  phoneCountryUrn?: string;
  phoneTimezoneUrn?: string;
  phoneNumber?: string;
  textTimeDate?: Date;
  textTimeTime?: string;
}

const StyledContentPanel = styled(ContentPanel)`
  margin-bottom: 19px;
  padding-bottom: 5px;
`;

interface IProps {
  errors: FormikErrors<IDefaultValuesModel>;
  touched: FormikTouched<IDefaultValuesModel>;
  values: IDefaultValuesModel;
  surchargeNotice?: string;
  countryOptions?: ISelectOption[];
  timezoneOptions: ISelectOption[];
  hideSendDate?: boolean;
  hideTextDetails?: boolean;
  onChangeAddressCountry?(urn: string): void;
  onChangePhoneCountry?(urn: string): void;
  setFieldValue(field: keyof IDefaultValuesModel, value: string, shouldValidate?: boolean): void;
}

/**
 * Form section for video message recipient
 */
export const FormSectionLetterRecipient: React.FC<IProps> = ({
  errors,
  touched,
  values,
  surchargeNotice,
  countryOptions,
  timezoneOptions,
  hideSendDate = false,
  hideTextDetails = false,
  onChangePhoneCountry,
  onChangeAddressCountry,
  setFieldValue,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Row>
        <Col xl={6}>
          <FormFieldFirstNameFreeText />
        </Col>
        <Col xl={6}>
          <FormFieldLastName />
        </Col>
      </Row>
      <Row>
        <Col xl={6}>
          <FormFieldBoyGirl />
        </Col>
        <Col xl={6}>
          <FormFieldAge errors={errors} touched={touched} />
        </Col>
      </Row>

      <AddressForm
        setFieldValue={setFieldValue}
        countryUrn={values.addressCountryUrn}
        onChangeCountry={onChangeAddressCountry}
        addressIsFilled={!!(values.addressTown && values.addressPostcode)}
      />

      {surchargeNotice && <Paragraph>{surchargeNotice}</Paragraph>}

      {!hideSendDate && (
        <Row>
          <Col xl={12}>
            <FormFieldDate minDate={earliestLetterSendDate()} maxDate={lastLetterSendDate()} />
          </Col>
        </Row>
      )}

      {!hideTextDetails && onChangePhoneCountry && countryOptions && (
        <StyledContentPanel colour="yellow">
          <Row>
            <Col>
              <Heading5>Santa needs details of how to send his digital Santa Letter</Heading5>
              <FormFieldPhoneNumber
                onChangeCountry={onChangePhoneCountry}
                countryOptions={countryOptions}
                label={t("letter.form.field.phone.label")}
                required={true}
              />
            </Col>
          </Row>
          {timezoneOptions.length && (
            <Row>
              <Col>
                <FormFieldWrapper label="Country and Time zone?" required name="phoneTimezoneUrn">
                  <Field
                    name="phoneTimezoneUrn"
                    component={FormikFormInputSelect}
                    options={timezoneOptions}
                    required
                  />
                </FormFieldWrapper>
              </Col>
            </Row>
          )}
          <Row>
            <Col xl={6}>
              <FormFieldDate
                id="textTimeDate"
                minDate={dateFns.startOfDay(new Date())}
                label="Date to send message"
              />
            </Col>
            {values.textTimeDate && (
              <Col xl={6}>
                <FormFieldMessageTime id={"textTimeTime"} />
              </Col>
            )}
          </Row>
        </StyledContentPanel>
      )}
    </>
  );
};
